import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/dah6p7roh653ivfvnmo0";
const MAX_LENGTH = 500;

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("phone_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessage("");
      return;
    }
    formData.delete("phone_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null =
        null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (!response.ok || parsed?.code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(String(serverMsg) || "提交未成功，请稍后重试");
        return;
      }

      setStatus("success");
      setFormError("");
      form.reset();
      setMessage("");
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请稍后再试一次");
    }
  };

  if (status === "success") {
    return (
      <div className="rounded-lg border border-secondary-300 bg-secondary-100 p-6 text-center">
        <i className="ri-checkbox-circle-line text-3xl text-secondary-700"></i>
        <h3 className="mt-3 font-heading text-lg font-semibold text-secondary-900">
          已收到你的反馈
        </h3>
        <p className="mt-2 text-sm text-secondary-900/80">
          我们通常会在两个工作日内回复，感谢你帮助我们把事件梳理做得更准确。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-4 cursor-pointer rounded-md border border-secondary-300 px-4 py-2 text-sm font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
        >
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-readdy-form
      id="hot-melon-contact-form"
      className="flex flex-col gap-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
            称呼
          </label>
          <input
            id="contact-name"
            type="text"
            name="name"
            required
            placeholder="怎么称呼你"
            className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
            邮箱地址
          </label>
          <input
            id="contact-email"
            type="email"
            name="email"
            required
            placeholder="you@example.com"
            autoComplete="email"
            className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="contact-topic" className="text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue="内容纠错"
          className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        >
          <option value="内容纠错">内容纠错</option>
          <option value="事件补充">事件补充</option>
          <option value="版权与下架">版权与下架</option>
          <option value="合作与投稿">合作与投稿</option>
          <option value="其他建议">其他建议</option>
        </select>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
          具体说明
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={MAX_LENGTH}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="请说明涉及的事件、页面与需要更正或补充的信息"
          className="resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
        />
        <span className="self-end text-xs text-foreground-500">
          {message.length}/{MAX_LENGTH}
        </span>
      </div>

      <input
        type="text"
        name="phone_alt"
        className="extra-field-guard"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === "error" && (
        <p className="flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-2.5 text-sm text-primary-700">
          <i className="ri-error-warning-line text-base"></i>
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <i
          className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}
        ></i>
        {status === "submitting" ? "提交中…" : "提交反馈"}
      </button>
      <p className="text-xs leading-relaxed text-foreground-500">
        我们只会使用你填写的邮箱回复本次反馈，不会用于其他用途。
      </p>
    </form>
  );
}